import React, { useEffect, useState } from 'react';
import { useReducedMotion } from 'framer-motion';
import { ArrowDownIcon, RotateCcwIcon } from 'lucide-react';
import { cn } from '../../src/lib/utils';
type LoopDiagramProps = {
  steps: readonly string[];
  center: React.ReactNode;
  tone?: 'light' | 'dark';
  ariaLabel: string;
  className?: string;
};

/** Circular process loop on md+; a vertical list that returns to the start on mobile. */
export function LoopDiagram({
  steps,
  center,
  tone = 'light',
  ariaLabel,
  className
}: LoopDiagramProps) {
  const dark = tone === 'dark';
  const reduceMotion = useReducedMotion();
  const [active, setActive] = useState(0);
  useEffect(() => {
    if (reduceMotion) return;
    const timer = window.setInterval(() => setActive(value => (value + 1) % steps.length), 1400);
    return () => window.clearInterval(timer);
  }, [reduceMotion, steps.length]);
  const nodeClass = (index: number) => cn('rounded-lg border px-3 py-2 text-center text-[13px] font-semibold uppercase tracking-[0.05em] transition-colors duration-300', index === active ? dark ? 'border-violet bg-violet text-white' : 'border-lime bg-lime text-white' : dark ? 'border-night-line bg-night-panel text-night-text' : 'border-hairline bg-panel text-text-primary');
  return <div className={className} data-id="element-1183" data-mp="1xpduccuw4see">
      {/* Desktop / tablet: circle */}
      <div className="relative mx-auto hidden aspect-square w-full max-w-[540px] md:block" data-id="element-1184" data-mp="2bpybz5p2ki6b">
        <div aria-hidden="true" className={cn('absolute inset-[13%] rounded-full border border-dashed', dark ? 'border-night-line' : 'border-hairline')} data-id="element-1185" data-mp="26s55scnrno61" />
        <div className="absolute inset-[30%] flex items-center justify-center text-center" data-id="element-1186" data-mp="2ghosjtelo266">{center}</div>
        <ol aria-label={ariaLabel} data-id="element-1187" data-mp="2ugy5mk7ieg5x">
          {steps.map((step, index) => {
          const angle = index / steps.length * Math.PI * 2 - Math.PI / 2;
          const left = 50 + 40 * Math.cos(angle);
          const top = 50 + 40 * Math.sin(angle);
          return <li key={step} className="absolute w-[34%] max-w-[170px] -translate-x-1/2 -translate-y-1/2" style={{
            left: `${left}%`,
            top: `${top}%`
          }} data-id="element-1188" data-mp="1s3tasxag9ao3" data-mpi={index}>
                <div className={nodeClass(index)} data-id="element-1189" data-mp="20vvb8vkiqqyd">{step}</div>
              </li>;
        })}
        </ol>
      </div>

      {/* Mobile: vertical */}
      <div className="md:hidden" data-id="element-1190" data-mp="25uyh2i8bp17e">
        <div className="mb-6 text-center" data-id="element-1191" data-mp="2au18v0zalaxf">{center}</div>
        <ol aria-label={ariaLabel} className="flex flex-col items-stretch" data-id="element-1192" data-mp="2asgebbcz9rzk">
          {steps.map((step, index) => <li key={step} className="flex flex-col items-center" data-id="element-1193" data-mp="2frjkx08lw69n" data-mpi={index}>
              <div className={cn(nodeClass(index), 'w-full')} data-id="element-1194" data-mp="2jto9hkk76cpf">{step}</div>
              {index < steps.length - 1 ? <ArrowDownIcon className={cn('my-1.5 h-4 w-4', dark ? 'text-night-faint' : 'text-text-tertiary')} aria-hidden="true" data-id="element-1195" data-mp="lhfxeete76iw" /> : null}
            </li>)}
        </ol>
        <p className={cn('mt-3 flex items-center justify-center gap-2 text-[13px] font-medium', dark ? 'text-violet-ink' : 'text-lime')} data-id="element-1196" data-mp="hyqn3up0slim">
          <RotateCcwIcon className="h-4 w-4" aria-hidden="true" data-id="element-1197" data-mp="87cn7lt5pjoy" />
          Returns to {steps[0]}
        </p>
      </div>
    </div>;
}